import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  Alert,
  IconButton,
  Paper,
  Avatar,
  Chip,
  CircularProgress,
  Stack
} from '@mui/material';
import { PhotoCamera, Map, LocationOn, Info, MyLocation, Search, GpsFixed, Close } from '@mui/icons-material';
import { ODP, ODC, Router, VLANInterface } from '../types';
import odpService from '../services/odpService';
import odcService from '../services/odcService';
import routerService from '../services/routerService';

interface ODPFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (odp: Partial<ODP>, photo?: File | null) => void;
  odp?: ODP | null;
  title: string;
}

interface VLANInfo {
  id: string;
  name: string;
  vlanId: number;
  interface: string;
  disabled: boolean;
}

const ODPForm: React.FC<ODPFormProps> = ({
  open,
  onClose,
  onSubmit,
  odp,
  title
}) => {
  const [formData, setFormData] = useState<Partial<ODP>>({
    odpCode: '',
    fullCode: '',
    odcId: '',
    routerId: '',
    vlanId: 0,
    vlanName: '',
    latitude: 0,
    longitude: 0,
    radius: 50,
    address: '',
    isActive: true,
    photo: ''
  });

  const [odcs, setODCs] = useState<ODC[]>([]);
  const [routers, setRouters] = useState<Router[]>([]);
  const [vlans, setVLANs] = useState<VLANInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [previewPhoto, setPreviewPhoto] = useState<string>('');
  const [showVLANDialog, setShowVLANDialog] = useState(false);
  const [selectedRouter, setSelectedRouter] = useState<Router | null>(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [mapDialogOpen, setMapDialogOpen] = useState(false);
  const [searchAddress, setSearchAddress] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<{lat: number, lng: number} | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [mapZoom, setMapZoom] = useState(15);
  const [mapCenter, setMapCenter] = useState({lat: -6.2088, lng: 106.8456});
  const [isDraggingMap, setIsDraggingMap] = useState(false);

  // Generate full ODP code based on selected ODC
  const generateFullODPCode = (odcId: string, odpCode: string) => {
    const selectedODC = odcs.find(odc => odc.id === odcId);
    if (selectedODC && odpCode) {
      return `${selectedODC.fullCode}-${odpCode}`;
    }
    return odpCode;
  };

  useEffect(() => {
    if (open) {
      fetchODCs();
      fetchRouters();
      if (odp) {
        setFormData(odp);
        if (odp.photo) {
          // Use direct URL to bypass API prefix
          const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:3000';
          setPreviewPhoto(`${baseUrl}/uploads/odp/${odp.photo}`);
        }
      } else {
        resetForm();
      }
    }
  }, [open, odp]);

  const resetForm = () => {
    setFormData({
      odpCode: '',
      fullCode: '',
      odcId: '',
      routerId: '',
      vlanId: 0,
      vlanName: '',
      latitude: 0,
      longitude: 0,
      radius: 50,
      address: '',
      isActive: true,
      photo: ''
    });
    setPhotoFile(null);
    setPreviewPhoto('');
    setError('');
  };

  const fetchODCs = async () => {
    try {
      const response = await odcService.getODCs({ limit: 100 });
      setODCs(response.data.odcs);
    } catch (err) {
      console.error('Error fetching ODCs:', err);
      setError('Failed to load ODCs');
    }
  };

  const fetchRouters = async () => {
    try {
      const response = await routerService.getRouters(1, 100);
      setRouters(response.data.routers || []);
    } catch (err) {
      console.error('Error fetching routers:', err);
      setError('Failed to load routers');
    }
  };

  const fetchVLANs = async (routerId: string) => {
    try {
      setLoading(true);
      const response = await odpService.getRouterVLANs(routerId);
      setVLANs(response.data || []);
      setShowVLANDialog(true);
    } catch (err) {
      console.error('Error fetching VLANs:', err);
      setError('Failed to load VLANs from router');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setError('Photo size must be less than 5MB');
        return;
      }
      
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onload = () => {
        setPreviewPhoto(reader.result as string);
      };
      reader.readAsDataURL(file);
      setError('');
    }
  };

  const openMaps = () => {
    if (formData.latitude && formData.longitude) {
      const url = `https://www.google.com/maps?q=${formData.latitude},${formData.longitude}`;
      window.open(url, '_blank');
    } else {
      setError('Please enter coordinates first');
    }
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError('Geolocation tidak didukung oleh browser ini');
      return;
    }

    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setFormData(prev => ({
          ...prev,
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        }));
        setGettingLocation(false);
        setError('');
      },
      (error) => {
        setError('Gagal mendapatkan lokasi: ' + error.message);
        setGettingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };

  const openMapDialog = () => {
    setMapDialogOpen(true);
    setSearchAddress('');
    setSearchResults([]);
    setSelectedLocation(null);
  };

  const closeMapDialog = () => {
    setMapDialogOpen(false);
    setSearchAddress('');
    setSearchResults([]);
    setSelectedLocation(null);
  };

  const searchLocation = async () => {
    if (!searchAddress.trim()) return;
    
    setSearchLoading(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchAddress)}&limit=5&countrycodes=id`
      );
      const data = await response.json();
      
      const results = data.map((item: any) => ({
        display_name: item.display_name,
        lat: parseFloat(item.lat),
        lon: parseFloat(item.lon),
      }));
      
      setSearchResults(results);
    } catch (err) {
      setError('Gagal mencari lokasi. Silakan coba lagi.');
    } finally {
      setSearchLoading(false);
    }
  };

  const selectLocation = (location: {lat: number, lon: number}) => {
    setSelectedLocation({lat: location.lat, lng: location.lon});
    setMapCenter({lat: location.lat, lng: location.lon});
    setMapZoom(18);
  };

  const confirmLocation = () => {
    if (selectedLocation) {
      setFormData(prev => ({
        ...prev,
        latitude: selectedLocation.lat,
        longitude: selectedLocation.lng
      }));
      closeMapDialog();
    }
  };

  const openInMaps = () => {
    if (formData.latitude && formData.longitude) {
      const url = `https://www.google.com/maps?q=${formData.latitude},${formData.longitude}`;
      window.open(url, '_blank');
    }
  };

  const selectVLAN = (vlan: VLANInfo) => {
    setFormData(prev => ({
      ...prev,
      vlanId: vlan.vlanId,
      vlanName: vlan.name
    }));
    setShowVLANDialog(false);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      // Validation
      if (!formData.odpCode || !formData.odcId) {
        setError('Please fill in all required fields');
        return;
      }

      let submitData = { ...formData };

      // Generate full code before submission
      if (formData.odcId && formData.odpCode) {
        submitData.fullCode = generateFullODPCode(formData.odcId, formData.odpCode);
      }

      // Pass both data and photo file to parent
      onSubmit(submitData, photoFile);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save ODP');
    } finally {
      setLoading(false);
    }
  };

  const selectedODC = odcs.find(odc => odc.id === formData.odcId);
  const selectedRouterData = routers.find(router => router.id.toString() === formData.routerId);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            {/* Basic Information */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl sx={{ flex: '1 1 300px' }} required>
                <InputLabel>ODC</InputLabel>
                <Select
                  value={formData.odcId || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, odcId: e.target.value }))}
                  label="ODC"
                >
                  {odcs.map((odc) => (
                    <MenuItem key={odc.id} value={odc.id}>
                      {odc.odcCode} - {odc.fullCode}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                sx={{ flex: '1 1 300px' }}
                label="ODP Code"
                value={formData.odpCode || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, odpCode: e.target.value }))}
                required
                placeholder="Contoh: ODP-001"
                helperText={formData.odcId ? `Full Code: ${generateFullODPCode(formData.odcId, formData.odpCode || '')}` : 'Pilih ODC terlebih dahulu'}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ flex: '1 1 300px', display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive || false}
                      onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                    />
                  }
                  label="Active"
                />
              </Box>
            </Box>

            {/* Router and VLAN */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Router & VLAN Configuration
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl sx={{ flex: '1 1 300px' }}>
                <InputLabel>Router</InputLabel>
                <Select
                  value={formData.routerId || ''}
                  onChange={(e) => {
                    const routerId = e.target.value;
                    setFormData(prev => ({ ...prev, routerId }));
                    const router = routers.find(r => r.id.toString() === routerId);
                    setSelectedRouter(router || null);
                  }}
                  label="Router"
                >
                  {routers.map((router) => (
                    <MenuItem key={router.id} value={router.id.toString()}>
                      {router.name} - {router.ip_address}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flex: '1 1 300px' }}>
                <TextField
                  fullWidth
                  label="VLAN"
                  value={formData.vlanName || ''}
                  InputProps={{
                    readOnly: true,
                  }}
                  placeholder="Select VLAN from router"
                />
                <Button
                  variant="outlined"
                  onClick={() => {
                    if (formData.routerId) {
                      fetchVLANs(formData.routerId);
                    } else {
                      setError('Please select a router first');
                    }
                  }}
                  disabled={!formData.routerId || loading}
                >
                  {loading ? 'Loading...' : 'Select VLAN'}
                </Button>
              </Box>
            </Box>

            {/* Location */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Informasi Lokasi
              </Typography>
            </Box>

            <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Stack direction="row" spacing={2} alignItems="center" mb={2} flexWrap="wrap">
                <LocationOn color="primary" />
                <Typography variant="subtitle2">
                  Koordinat Lokasi ODP
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={gettingLocation ? <CircularProgress size={16} /> : <GpsFixed />}
                  onClick={getCurrentLocation}
                  disabled={gettingLocation}
                >
                  {gettingLocation ? 'Mendapatkan Lokasi...' : 'GPS Saat Ini'}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Search />}
                  onClick={openMapDialog}
                  color="secondary"
                >
                  Cari Lokasi
                </Button>
                {formData.latitude && formData.longitude && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Map />}
                    onClick={openInMaps}
                  >
                    Lihat di Maps
                  </Button>
                )}
              </Stack>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  label="Latitude"
                  type="number"
                  value={formData.latitude || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, latitude: parseFloat(e.target.value) || 0 }))}
                  required
                  inputProps={{ step: 'any', min: -90, max: 90 }}
                  placeholder="-6.200000"
                  helperText="Koordinat lintang (-90 hingga 90)"
                />
                <TextField
                  fullWidth
                  label="Longitude"
                  type="number"
                  value={formData.longitude || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, longitude: parseFloat(e.target.value) || 0 }))}
                  required
                  inputProps={{ step: 'any', min: -180, max: 180 }}
                  placeholder="106.816666"
                  helperText="Koordinat bujur (-180 hingga 180)"
                />
                <TextField
                  fullWidth
                  label="Radius Coverage (meters)"
                  type="number"
                  value={formData.radius || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, radius: parseInt(e.target.value) || 50 }))}
                  required
                  inputProps={{ min: 1, max: 10000 }}
                  placeholder="50"
                  helperText="Jangkauan coverage ODP dalam meter (1-10000)"
                />
              </Stack>
            </Paper>

            <TextField
              fullWidth
              label="Address"
              multiline
              rows={2}
              value={formData.address || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
            />

            {/* Photo Upload */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Photo
              </Typography>
            </Box>

            <Box>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="photo-upload"
                  type="file"
                  onChange={handlePhotoChange}
                />
                <label htmlFor="photo-upload">
                  <IconButton color="primary" aria-label="upload picture" component="span">
                    <PhotoCamera />
                  </IconButton>
                </label>
                <Typography variant="body2">
                  Upload ODP photo (max 5MB)
                </Typography>
              </Box>
              
              {previewPhoto && (
                <Paper sx={{ p: 2, mt: 2, textAlign: 'center' }}>
                  <Avatar
                    src={previewPhoto}
                    sx={{ width: 150, height: 150, mx: 'auto' }}
                    variant="rounded"
                  />
                </Paper>
              )}
            </Box>

            {/* ODC Information */}
            {selectedODC && (
              <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Selected ODC Information:
                </Typography>
                <Typography variant="body2">
                  {selectedODC.odcCode} - {selectedODC.fullCode}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Address: {selectedODC.address}
                </Typography>
              </Paper>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Map Search Dialog */}
      <Dialog open={mapDialogOpen} onClose={closeMapDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Pilih Lokasi ODP</Typography>
            <IconButton onClick={closeMapDialog}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3}>
            {/* Search Box */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Cari Alamat:
              </Typography>
              <Box display="flex" gap={1}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Ketik alamat di Indonesia..."
                  value={searchAddress}
                  onChange={(e) => setSearchAddress(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && searchLocation()}
                />
                <Button
                  variant="contained"
                  onClick={searchLocation}
                  disabled={searchLoading || !searchAddress.trim()}
                  startIcon={searchLoading ? <CircularProgress size={16} /> : <Search />}
                >
                  {searchLoading ? 'Mencari...' : 'Cari'}
                </Button>
              </Box>
            </Box>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Hasil Pencarian:
                </Typography>
                <Stack spacing={1} maxHeight={200} sx={{ overflowY: 'auto' }}>
                  {searchResults.map((result, index) => (
                    <Paper
                      key={index}
                      sx={{
                        p: 2,
                        cursor: 'pointer',
                        border: selectedLocation?.lat === result.lat && selectedLocation?.lng === result.lon 
                          ? '2px solid' 
                          : '1px solid',
                        borderColor: selectedLocation?.lat === result.lat && selectedLocation?.lng === result.lon 
                          ? 'primary.main' 
                          : 'grey.300',
                        '&:hover': {
                          bgcolor: 'grey.50',
                        },
                      }}
                      onClick={() => selectLocation(result)}
                    >
                      <Typography variant="body2" fontWeight="medium">
                        {result.display_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                        {result.lat.toFixed(6)}, {result.lon.toFixed(6)}
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Interactive Map Container */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Peta Interaktif - Klik untuk menandai lokasi:
              </Typography>
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: 400,
                  border: '2px solid',
                  borderColor: 'grey.300',
                  borderRadius: 2,
                  overflow: 'hidden',
                  backgroundColor: 'grey.100',
                }}
              >
                {/* OpenStreetMap iframe */}
                <iframe
                  src={`https://www.openstreetmap.org/export/embed.html?bbox=${
                    mapCenter.lng - (0.001 * Math.pow(2, 18 - mapZoom))
                  },${
                    mapCenter.lat - (0.001 * Math.pow(2, 18 - mapZoom))
                  },${
                    mapCenter.lng + (0.001 * Math.pow(2, 18 - mapZoom))
                  },${
                    mapCenter.lat + (0.001 * Math.pow(2, 18 - mapZoom))
                  }&layer=mapnik`}
                  width="100%"
                  height="100%"
                  style={{ border: 'none' }}
                  title="Interactive Map"
                />
                
                {/* Custom Draggable Marker */}
                {selectedLocation && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: `${50 + ((selectedLocation.lng - mapCenter.lng) / (0.001 * Math.pow(2, 18 - mapZoom) * 2)) * 50}%`,
                      top: `${50 - ((selectedLocation.lat - mapCenter.lat) / (0.001 * Math.pow(2, 18 - mapZoom) * 2)) * 50}%`,
                      transform: 'translate(-50%, -100%)',
                      cursor: 'move',
                      zIndex: 1001,
                      transition: 'all 0.1s ease',
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      const startX = e.clientX;
                      const startY = e.clientY;
                      const startLat = selectedLocation.lat;
                      const startLng = selectedLocation.lng;
                      const rect = e.currentTarget.parentElement!.getBoundingClientRect();
                      
                      const handleMouseMove = (moveEvent: MouseEvent) => {
                        const deltaX = moveEvent.clientX - startX;
                        const deltaY = moveEvent.clientY - startY;
                        
                        // Convert pixel movement to coordinate change
                        const mapWidth = rect.width;
                        const mapHeight = rect.height;
                        const zoomFactor = 0.001 * Math.pow(2, 18 - mapZoom);
                        
                        const latChange = -(deltaY / mapHeight) * zoomFactor * 2;
                        const lngChange = (deltaX / mapWidth) * zoomFactor * 2;
                        
                        setSelectedLocation({
                          lat: startLat + latChange,
                          lng: startLng + lngChange,
                        });
                      };
                      
                      const handleMouseUp = () => {
                        document.removeEventListener('mousemove', handleMouseMove);
                        document.removeEventListener('mouseup', handleMouseUp);
                      };
                      
                      document.addEventListener('mousemove', handleMouseMove);
                      document.addEventListener('mouseup', handleMouseUp);
                    }}
                  >
                    <LocationOn sx={{ fontSize: 40, color: 'red' }} />
                  </Box>
                )}
                
                {/* Clickable overlay for map interaction */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 1000,
                    cursor: isDraggingMap ? 'grabbing' : 'crosshair',
                  }}
                  onMouseDown={(e) => {
                    if (e.ctrlKey || e.shiftKey) {
                      // Pan map mode
                      e.preventDefault();
                      setIsDraggingMap(true);
                      const startX = e.clientX;
                      const startY = e.clientY;
                      const startMapCenter = { ...mapCenter };
                      const rect = e.currentTarget.getBoundingClientRect(); // Store rect reference
                      
                      const handleMapDrag = (dragEvent: MouseEvent) => {
                        const deltaX = dragEvent.clientX - startX;
                        const deltaY = dragEvent.clientY - startY;
                        
                        const mapWidth = rect.width;
                        const mapHeight = rect.height;
                        const zoomFactor = 0.001 * Math.pow(2, 18 - mapZoom);
                        
                        const latChange = (deltaY / mapHeight) * zoomFactor * 2;
                        const lngChange = -(deltaX / mapWidth) * zoomFactor * 2;
                        
                        setMapCenter({
                          lat: startMapCenter.lat + latChange,
                          lng: startMapCenter.lng + lngChange,
                        });
                      };
                      
                      const handleMapDragEnd = () => {
                        setIsDraggingMap(false);
                        document.removeEventListener('mousemove', handleMapDrag);
                        document.removeEventListener('mouseup', handleMapDragEnd);
                      };
                      
                      document.addEventListener('mousemove', handleMapDrag);
                      document.addEventListener('mouseup', handleMapDragEnd);
                    }
                  }}
                  onClick={(e) => {
                    // Only place marker if not dragging map and no modifier keys pressed
                    if (!isDraggingMap && !e.ctrlKey && !e.shiftKey) {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const x = e.clientX - rect.left;
                      const y = e.clientY - rect.top;
                      
                      const mapWidth = rect.width;
                      const mapHeight = rect.height;
                      
                      const zoomFactor = 0.001 * Math.pow(2, 18 - mapZoom);
                      
                      const lat = mapCenter.lat - ((y / mapHeight - 0.5) * zoomFactor * 2);
                      const lng = mapCenter.lng + ((x / mapWidth - 0.5) * zoomFactor * 2);
                      
                      setSelectedLocation({ lat, lng });
                    }
                  }}
                />
                
                {/* Crosshair for precise clicking (only when no marker) */}
                {!selectedLocation && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      pointerEvents: 'none',
                      zIndex: 999,
                    }}
                  >
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        border: '2px solid red',
                        borderRadius: '50%',
                        backgroundColor: 'rgba(255, 0, 0, 0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Box
                        sx={{
                          width: 4,
                          height: 4,
                          backgroundColor: 'red',
                          borderRadius: '50%',
                        }}
                      />
                    </Box>
                  </Box>
                )}
                
                {/* Grid Lines for better precision (optional) */}
                {selectedLocation && (
                  <>
                    {/* Vertical line */}
                    <Box
                      sx={{
                        position: 'absolute',
                        left: `${50 + ((selectedLocation.lng - mapCenter.lng) / (0.001 * Math.pow(2, 18 - mapZoom) * 2)) * 50}%`,
                        top: 0,
                        bottom: 0,
                        width: '1px',
                        backgroundColor: 'rgba(255, 0, 0, 0.3)',
                        pointerEvents: 'none',
                        zIndex: 997,
                      }}
                    />
                    {/* Horizontal line */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: `${50 - ((selectedLocation.lat - mapCenter.lat) / (0.001 * Math.pow(2, 18 - mapZoom) * 2)) * 50}%`,
                        left: 0,
                        right: 0,
                        height: '1px',
                        backgroundColor: 'rgba(255, 0, 0, 0.3)',
                        pointerEvents: 'none',
                        zIndex: 997,
                      }}
                    />
                  </>
                )}
                
                {/* Instructions Overlay */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 10,
                    left: 10,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    color: 'white',
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: '11px',
                    zIndex: 1000,
                    maxWidth: '200px',
                  }}
                >
                  🖱️ {isDraggingMap ? 'Dragging Map...' : 'Ctrl/Shift + Drag = Pan Map'}
                </Box>
                
                {/* Current Location Indicator */}
                {selectedLocation && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 10,
                      left: 10,
                      backgroundColor: 'rgba(76, 175, 80, 0.9)',
                      color: 'white',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: '11px',
                      zIndex: 1000,
                    }}
                  >
                    📍 {selectedLocation.lat.toFixed(6)}, {selectedLocation.lng.toFixed(6)}
                  </Box>
                )}
                
                {/* Zoom Level Indicator */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 10,
                    right: 10,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    color: 'white',
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: '12px',
                    zIndex: 1000,
                  }}
                >
                  🔍 Zoom: {mapZoom}
                </Box>
                
                {/* Zoom Controls */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 10,
                    right: 10,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    zIndex: 1000,
                  }}
                >
                  <Button
                    size="small"
                    variant="contained"
                    sx={{ minWidth: 30, width: 30, height: 30 }}
                    onClick={() => setMapZoom(prev => Math.min(prev + 1, 18))}
                    disabled={mapZoom >= 18}
                  >
                    +
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    sx={{ minWidth: 30, width: 30, height: 30 }}
                    onClick={() => setMapZoom(prev => Math.max(prev - 1, 1))}
                    disabled={mapZoom <= 1}
                  >
                    -
                  </Button>
                </Box>
              </Box>
            </Box>

            {/* Selected Location Preview */}
            {selectedLocation && (
              <Paper sx={{ p: 2, bgcolor: 'primary.50' }}>
                <Typography variant="subtitle2" color="primary.main" gutterBottom>
                  Lokasi Terpilih:
                </Typography>
                <Typography variant="body2" fontFamily="monospace">
                  Latitude: {selectedLocation.lat.toFixed(6)}
                </Typography>
                <Typography variant="body2" fontFamily="monospace">
                  Longitude: {selectedLocation.lng.toFixed(6)}
                </Typography>
                <Button
                  size="small"
                  startIcon={<Map />}
                  onClick={() => {
                    const url = `https://www.google.com/maps?q=${selectedLocation.lat},${selectedLocation.lng}`;
                    window.open(url, '_blank');
                  }}
                  sx={{ mt: 1 }}
                >
                  Preview di Google Maps
                </Button>
              </Paper>
            )}

            {/* Instructions */}
            <Alert severity="info">
              <Typography variant="body2">
                💡 <strong>Cara menggunakan peta interaktif:</strong>
                <br />
                <strong>Metode 1:</strong> Ketik alamat → Pilih dari hasil → Klik "Gunakan Lokasi Ini"
                <br />
                <strong>Metode 2:</strong> Klik langsung di peta untuk menandai lokasi
                <br />
                <strong>Metode 3:</strong> Drag marker yang sudah ada untuk menyesuaikan posisi
                <br />
                <strong>Pan Map:</strong> Tahan Ctrl/Shift + Drag untuk menggeser peta
                <br />
                <strong>Zoom:</strong> Gunakan tombol +/- di kanan atas peta
              </Typography>
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeMapDialog}>
            Batal
          </Button>
          <Button 
            variant="contained" 
            onClick={confirmLocation}
            disabled={!selectedLocation}
            startIcon={<LocationOn />}
          >
            Gunakan Lokasi Ini
          </Button>
        </DialogActions>
      </Dialog>

      {/* VLAN Selection Dialog */}
      <Dialog open={showVLANDialog} onClose={() => setShowVLANDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Select VLAN from {selectedRouterData?.name}
        </DialogTitle>
        <DialogContent>
          {vlans.length === 0 ? (
            <Typography>No VLANs found on this router</Typography>
          ) : (
            <Box sx={{ mt: 1 }}>
              {vlans.map((vlan) => (
                <Paper
                  key={vlan.id}
                  sx={{
                    p: 2,
                    mb: 1,
                    cursor: 'pointer',
                    opacity: vlan.disabled ? 0.5 : 1,
                    '&:hover': { backgroundColor: 'grey.50' }
                  }}
                  onClick={() => !vlan.disabled && selectVLAN(vlan)}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle2">
                        {vlan.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Interface: {vlan.interface}
                      </Typography>
                    </Box>
                    <Chip label={`VLAN: ${vlan.vlanId}`} size="small" />
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowVLANDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ODPForm;
